/**
 * Calendar-date helpers that operate on `'YYYY-MM-DD'` strings and local-time
 * `Date` components only.
 *
 * Why strings, never `Date`, in state: a meal-plan `plan_date` is a *calendar
 * date*, not an instant. Zürich is UTC+1/+2, so any round-trip through
 * `Date.toISOString()` silently shifts the day
 * (`new Date(2026, 6, 27).toISOString()` → `'2026-07-26'`). Keeping the key as a
 * string and only ever building a *local* `Date` at the formatting boundary
 * avoids the whole class of off-by-one-day bugs.
 *
 * Lexicographic order on `'YYYY-MM-DD'` equals chronological order, so plain
 * string comparison is a valid date comparison here.
 */

/** Local calendar date of `d` as `'YYYY-MM-DD'` (uses local components, not UTC). */
export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today's local calendar date as a key. */
export function todayKey(): string {
  return toDateKey(new Date());
}

/** `key` shifted by `days` (may be negative), staying on local calendar dates. */
export function addDaysKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, m - 1, d); // local midnight — no TZ conversion
  dt.setDate(dt.getDate() + days);
  return toDateKey(dt);
}

/** Inclusive list of date keys from `from` to `to`. Empty if `from > to`. */
export function enumerateRange(from: string, to: string): string[] {
  const out: string[] = [];
  let cur = from;
  // Guard against a malformed range to avoid an unbounded loop.
  let guard = 0;
  while (cur <= to && guard < 1000) {
    out.push(cur);
    cur = addDaysKey(cur, 1);
    guard++;
  }
  return out;
}

/**
 * A *local-time* `Date` for a key — for feeding Angular's `DatePipe` only.
 * Never store the result in state; build it at the moment of formatting.
 */
export function keyToLocalDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}
